import React from 'react';
import { Input, Row, Col } from 'antd';
import { useIntl } from 'react-intl';

import type { CertificatePaymentFormVariant, PaymentFormValues } from '../../types';

import { FormField } from '../form-field';
import { FormSection } from '../form-section';
import { CertificateInlineSignupFields } from '../certificate-inline-signup-fields';
import { MAX_LENGTH_NAME } from '../../constants';
import { usePaymentFormValidationRules } from '../../payment-form-validation-context';

import formStyles from '../../styles.module.scss';

interface CertificateHolderSectionProps {
  initialValues: Partial<Pick<PaymentFormValues, 'firstName' | 'lastName' | 'email'>>;
  variant?: CertificatePaymentFormVariant;
  requiresInlineSignup?: boolean;
}

function CertificateHolderSectionEditable({
  initialValues,
  requiresInlineSignup = false,
}: CertificateHolderSectionProps): React.ReactElement {
  const intl = useIntl();
  const validationRules = usePaymentFormValidationRules();

  return (
    <FormSection
      title={intl.formatMessage({ id: 'certificates.payment.holderSection' })}
      hint={intl.formatMessage({ id: 'certificates.payment.holderHint' })}
      dividerAfter={!requiresInlineSignup}
    >
      <Row gutter={10} className={formStyles.certificateHolderRow}>
        <Col flex="1" className={formStyles.colMinWidth}>
          <FormField
            name="firstName"
            label={intl.formatMessage({ id: 'certificates.payment.firstName' })}
            variant="input"
            rules={validationRules.firstName}
            className={formStyles.formItem}
          >
            <Input
              placeholder={intl.formatMessage({ id: 'certificates.payment.firstNamePlaceholder' })}
              defaultValue={initialValues.firstName}
              maxLength={MAX_LENGTH_NAME}
            />
          </FormField>
        </Col>
        <Col flex="1" className={formStyles.colMinWidth}>
          <FormField
            name="lastName"
            label={intl.formatMessage({ id: 'certificates.payment.lastName' })}
            variant="input"
            validateTrigger="onBlur"
            rules={validationRules.lastName}
            className={formStyles.formItem}
          >
            <Input
              placeholder={intl.formatMessage({ id: 'certificates.payment.lastNamePlaceholder' })}
              defaultValue={initialValues.lastName}
              maxLength={MAX_LENGTH_NAME}
            />
          </FormField>
        </Col>
      </Row>
      <FormField
        name="email"
        label={intl.formatMessage({ id: 'certificates.payment.email' })}
        variant="input"
        validateTrigger="onBlur"
        rules={validationRules.email}
        className={formStyles.formItem}
      >
        <Input
          type="email"
          placeholder={intl.formatMessage({ id: 'certificates.payment.emailPlaceholder' })}
          defaultValue={initialValues.email}
        />
      </FormField>
    </FormSection>
  );
}

function CertificateHolderSectionPublic({
  initialValues,
}: CertificateHolderSectionProps): React.ReactElement {
  const intl = useIntl();
  const validationRules = usePaymentFormValidationRules();

  return (
    <FormSection
      title={intl.formatMessage({ id: 'certificates.payment.holderSection' })}
      hint={intl.formatMessage({ id: 'certificates.payment.holderHint' })}
      dividerAfter
    >
      <FormField
        name="email"
        label={intl.formatMessage({ id: 'certificates.payment.email' })}
        variant="input"
        rules={validationRules.email}
        className={formStyles.formItem}
      >
        <Input type="email" disabled defaultValue={initialValues.email} />
      </FormField>
    </FormSection>
  );
}

export function CertificateHolderSection({
  initialValues,
  variant = 'template',
  requiresInlineSignup = false,
}: CertificateHolderSectionProps): React.ReactElement {
  if (variant === 'public') {
    return <CertificateHolderSectionPublic initialValues={initialValues} />;
  }

  return (
    <>
      <CertificateHolderSectionEditable
        initialValues={initialValues}
        requiresInlineSignup={requiresInlineSignup}
      />
      {requiresInlineSignup ? <CertificateInlineSignupFields /> : null}
    </>
  );
}
